import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";

const LOGO = "/logo.png";

const NAV_ITEMS = [
  { label: "Product", id: "solution" },
  { label: "Workflow", id: "workflow" },
  { label: "Intelligence", id: "ai" },
  { label: "Pricing", id: "pricing" },
  { label: "FAQ", id: "faq" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#050505]/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1360px] px-6 md:px-10">
        <div className="flex h-16 md:h-[72px] items-center justify-between">
          {/* Brand */}
          <a
            data-testid="nav-brand"
            href="/"
            className="group flex items-center gap-3"
          >
            <span className="relative flex h-9 w-9 items-center justify-center rounded-xl overflow-hidden ring-1 ring-white/10 bg-[#0F172A]">
              <img src={LOGO} alt="SocialLead AI" className="h-9 w-9 object-cover" />
              <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(circle_at_50%_50%,rgba(124,58,237,0.5),transparent_70%)]" />
            </span>
            <span className="font-display text-[17px] tracking-tight font-semibold">
              SocialLead <span className="text-white/40">AI</span>
            </span>
          </a>

          {/* Center nav */}
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.02] px-1.5 py-1.5">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.id}
                data-testid={`nav-link-${item.id}`}
                href={`/#${item.id}`}
                className="relative rounded-full px-4 py-1.5 text-sm text-white/70 hover:text-white transition-colors group"
              >
                {item.label}
                <span className="absolute inset-0 rounded-full bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ))}
            <button
              data-testid="nav-resources"
              className="relative flex items-center gap-1 rounded-full px-4 py-1.5 text-sm text-white/70 hover:text-white transition-colors group"
            >
              Resources <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              <span className="absolute inset-0 rounded-full bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-2">
            <a
              data-testid="nav-signin"
              href="#"
              className="text-sm text-white/70 hover:text-white px-3 py-2 transition-colors"
            >
              Sign in
            </a>
            <a
              data-testid="nav-cta"
              href="/#pricing"
              className="group inline-flex items-center gap-2 rounded-full bg-white text-black px-4 py-2 text-sm font-medium hover:bg-white/90 transition-colors"
            >
              Start free trial
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* Mobile */}
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 bg-white/5"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl"
          >
            <nav aria-label="Mobile" className="px-6 py-6 flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.id}
                  href={`/#${item.id}`}
                  onClick={() => setOpen(false)}
                  className="py-3 text-lg font-display border-b border-white/5"
                >
                  {item.label}
                </a>
              ))}
              <a
                href="/#pricing"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-4 py-3 text-sm font-medium"
              >
                Start free trial <ArrowUpRight className="h-4 w-4" />
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
